import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  AlertTriangle, FileDown, ShieldAlert, ChefHat, Martini, Filter, X,
  Loader2, ListChecks, Search,
} from "lucide-react";
import api, { formatApiError, API } from "../api";
import { Button, Card, Input, Spinner, Badge, Select } from "../components/ui/Primitives";

function KpiCounter({ label, value, accent }) {
  return (
    <div className={`bg-bg-surface border ${accent ? "border-warn/30" : "border-white/10"} rounded-xl px-4 py-3`}>
      <div className="text-[10px] uppercase tracking-wider text-ink-muted font-medium">{label}</div>
      <div className="num text-xl font-medium text-ink-primary tracking-tight mt-1">{value}</div>
    </div>
  );
}

function DishDetail({ dish, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/allergens/${dish.kind}/${dish.id}`);
        if (alive) setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    return () => { alive = false; };
  }, [dish]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg-void/80 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-bg-surface border border-white/10 rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dish-allergen-detail">
        <div className="flex items-start justify-between p-5 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">{dish.kind === "recipe" ? t("allergens.recipe") : t("allergens.cocktail")}</div>
            <div className="text-base font-semibold text-ink-primary">{dish.name}</div>
            <div className="text-xs text-ink-secondary">{dish.category}</div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-primary p-1" data-testid="close-dish-detail"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          {!data ? <Spinner size={20} /> : (
            <>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-muted mb-2 font-medium flex items-center gap-1.5">
                  <ShieldAlert size={12} /> {t("allergens.contains_title")}
                </div>
                {Object.keys(data.contributors).length === 0 ? (
                  <div className="text-sm text-ink-muted">{t("allergens.none_detected")}</div>
                ) : (
                  <ul className="space-y-1.5">
                    {Object.entries(data.contributors).map(([k, names]) => (
                      <li key={k} className="text-sm flex items-start gap-2">
                        <Badge tone="warn">{t(`billing.module.${k}`, k)}</Badge>
                        <span className="text-ink-secondary">{[...new Set(names)].join(", ")}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {Object.keys(data.may_contain).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-warn mb-2 font-medium flex items-center gap-1.5">
                    <AlertTriangle size={12} /> {t("allergens.may_contain_title")}
                  </div>
                  <ul className="space-y-1.5">
                    {Object.entries(data.may_contain).map(([k, names]) => (
                      <li key={k} className="text-sm flex items-start gap-2">
                        <Badge>{k}</Badge>
                        <span className="text-ink-secondary">{[...new Set(names)].join(", ")}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AllergensPage() {
  const { t, i18n } = useTranslation();
  const [meta, setMeta] = useState(null);
  const [overview, setOverview] = useState(null);
  const [filterAllergen, setFilterAllergen] = useState("");
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [openDish, setOpenDish] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, o] = await Promise.all([api.get("/allergens/meta"), api.get("/allergens/overview")]);
        setMeta(m.data); setOverview(o.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const allergenLabel = (k) => {
    if (!meta) return k;
    const found = meta.allergens.find((a) => a.key === k);
    if (!found) return k;
    return i18n.language === "en" ? found.label_en : found.label_de;
  };

  const filteredDishes = useMemo(() => {
    if (!overview) return [];
    return overview.dishes.filter((d) => {
      if (filterAllergen && !d.allergens.includes(filterAllergen)) return false;
      if (kindFilter && d.kind !== kindFilter) return false;
      if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [overview, filterAllergen, kindFilter, search]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/allergens/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `allergenausweis_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("allergens.pdf_ready"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(false); }
  };

  if (!meta || !overview) {
    return <div className="flex justify-center py-16"><Spinner size={28} /></div>;
  }

  return (
    <div className="space-y-6" data-testid="allergens-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1.5">
            {t("nav.allergens")}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-ink-primary" data-testid="allergens-title">
            {t("allergens.title")}
          </h1>
          <p className="text-sm text-ink-secondary mt-2 max-w-2xl">{t("allergens.subtitle")}</p>
        </div>
        <Button onClick={downloadPdf} disabled={downloading} data-testid="allergens-pdf-button">
          {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
          {t("allergens.export_pdf")}
        </Button>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCounter label={t("allergens.kpi_dishes")} value={overview.total_dishes} />
        <KpiCounter label={t("allergens.kpi_recipes")} value={overview.total_recipes} />
        <KpiCounter label={t("allergens.kpi_cocktails")} value={overview.total_cocktails} />
        <KpiCounter
          label={t("allergens.kpi_with_allergens")}
          value={overview.dishes.filter((d) => d.allergens.length > 0).length}
          accent
        />
      </div>

      {/* Per-allergen counts */}
      <Card className="p-4" data-testid="allergens-counts">
        <div className="text-xs uppercase tracking-wider text-ink-muted mb-3 font-medium flex items-center gap-1.5">
          <ListChecks size={12} /> {t("allergens.counts_title")}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {meta.allergens.map((a) => {
            const cnt = overview.allergen_counts[a.key] || 0;
            const active = filterAllergen === a.key;
            return (
              <button
                key={a.key}
                onClick={() => setFilterAllergen(active ? "" : a.key)}
                data-testid={`allergen-chip-${a.key}`}
                className={`text-[11px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors ${
                  active
                    ? "bg-brand/15 border-brand/40 text-brand"
                    : cnt > 0
                    ? "bg-warn/5 border-warn/20 text-ink-primary hover:border-warn/40"
                    : "bg-white/[0.02] border-white/10 text-ink-muted"
                }`}
              >
                <span className="font-medium">{allergenLabel(a.key)}</span>
                <span className="num text-ink-muted">({cnt})</span>
              </button>
            );
          })}
          {filterAllergen && (
            <button onClick={() => setFilterAllergen("")} className="text-[11px] text-ink-muted hover:text-ink-primary inline-flex items-center gap-1 px-2 py-1" data-testid="allergens-clear-filter">
              <X size={11} /> {t("allergens.clear_filter")}
            </button>
          )}
        </div>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <Input className="pl-9" placeholder={t("allergens.search_placeholder")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="allergens-search" />
        </div>
        <Select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="allergens-kind-filter" className="w-40">
          <option value="">{t("allergens.all_kinds")}</option>
          <option value="recipe">{t("allergens.recipes_only")}</option>
          <option value="cocktail">{t("allergens.cocktails_only")}</option>
        </Select>
      </div>

      {/* Matrix table */}
      <div className="bg-bg-surface border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="allergens-matrix">
            <thead className="bg-white/[0.02] text-ink-muted text-[10px] uppercase tracking-wider sticky top-0">
              <tr>
                <th className="text-left px-3 py-2.5 w-10"></th>
                <th className="text-left px-3 py-2.5">{t("allergens.col_dish")}</th>
                {meta.allergens.map((a) => (
                  <th
                    key={a.key}
                    className="text-center px-1.5 py-2.5 w-9"
                    title={allergenLabel(a.key)}
                    data-testid={`allergens-col-${a.key}`}
                  >
                    {a.short}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredDishes.map((d) => (
                <tr key={`${d.kind}-${d.id}`} className="hover:bg-white/[0.02] cursor-pointer" onClick={() => setOpenDish(d)} data-testid={`dish-row-${d.id}`}>
                  <td className="px-3 py-2 text-ink-muted">
                    {d.kind === "recipe" ? <ChefHat size={14} /> : <Martini size={14} />}
                  </td>
                  <td className="px-3 py-2 text-ink-primary">
                    <div className="font-medium truncate max-w-[260px]">{d.name}</div>
                    <div className="text-[11px] text-ink-muted">{d.category}</div>
                  </td>
                  {meta.allergens.map((a) => {
                    const hit = d.allergens.includes(a.key);
                    return (
                      <td key={a.key} className="text-center px-1.5 py-2" data-testid={`cell-${d.id}-${a.key}`}>
                        {hit ? <span className="text-err text-lg leading-none" aria-label="contains">●</span> : <span className="text-ink-muted/30">·</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!filteredDishes.length && (
                <tr><td colSpan={meta.allergens.length + 2} className="px-4 py-10 text-center text-sm text-ink-muted">{t("allergens.no_dishes")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[11px] text-ink-muted leading-relaxed max-w-3xl">
        {t("allergens.legal_hint")}
      </div>

      {openDish && <DishDetail dish={openDish} onClose={() => setOpenDish(null)} />}
    </div>
  );
}
